import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuthContext } from '../../../contexts/AuthContext';
import PricingPlans from './PricingPlans';
import SubscriptionPlan from './SubscriptionPlan';
import BillingHistory from './BillingHistory';
import { subscriptionService, Subscription } from '../../../services/subscriptionService';

// Using Subscription interface from subscriptionService

const BillingManagement: React.FC = () => {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPlans, setShowPlans] = useState(false);
  const [processingAction, setProcessingAction] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { isDarkMode } = useTheme();
  const { user } = useAuthContext();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Always check for latest user data from auth context
    // Check for super admin access
    if (!user?.isPlatformAdmin) {
      setError(
        "You don't have permission to access this page. Only super administrators can manage subscriptions."
      );
      setLoading(false);
      return;
    }

    // Check for success parameter in URL
    const searchParams = new URLSearchParams(location.search);
    const success = searchParams.get('success');

    if (success === 'true') {
      setSuccessMessage(
        'Your subscription was successfully activated! You now have access to all features.'
      );
      // Remove the success parameter from the URL to prevent showing the message on refresh
      navigate('/settings/billing', { replace: true });
    }

    fetchSubscription();
  }, [location, navigate, user]);

  const fetchSubscription = async () => {
    try {
      setLoading(true);
      // Small delay to ensure webhook has processed the subscription if redirected from payment
      const subscription = await subscriptionService.getCurrentSubscription();
      setSubscription(subscription);

      // If no subscription is found, automatically show plans for new tenants
      if (!subscription) {
        console.log('No subscription found - new tenant detected');
        // Don't automatically show plans, but we'll make the button more prominent
      }

      setLoading(false);
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleSelectPlan = async (priceId: string) => {
    try {
      setProcessingAction(true);

      if (subscription) {
        // Update existing subscription
        console.log('Updating subscription with:', {
          id: subscription.id,
          stripeSubscriptionId: subscription.stripeSubscriptionId,
          price_id: priceId
        });

        // Check if we have a valid subscription ID
        if (!subscription.id) {
          console.error('Missing subscription ID');
          throw new Error('Invalid subscription data');
        }

        // Pass the database ID to the update function
        await subscriptionService.updateSubscription(subscription.stripeSubscriptionId!, priceId);
        await fetchSubscription();
        setShowPlans(false);
      } else {
        // Create new subscription
        const response = await subscriptionService.createSubscription(priceId);

        // If user needs to authenticate first
        if (response.requiresAuth && response.redirectUrl) {
          // Redirect to login page
          window.location.href = response.redirectUrl;
          return;
        }

        // If we have a checkout URL, redirect to it
        if (response.url) {
          window.location.href = response.url;
        } else {
          setError('Failed to create checkout session');
        }
        await fetchSubscription();
        setShowPlans(false);
      }

      setProcessingAction(false);
    } catch (err: any) {
      setError(err.message);
      setProcessingAction(false);
    }
  };

  // handleOpenCustomerPortal function removed as the Billing Portal button was removed

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500">{error}</p>
        <button
          onClick={fetchSubscription}
          className={`mt-4 px-4 py-2 rounded-md ${isDarkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white transition-colors`}
        >
          Try Again
        </button>
      </div>
    );
  }

  if (showPlans) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3
            className={`ml-6 mr-6 mt-6 text-lg font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}
          >
            Select a Plan
          </h3>
          <button
            onClick={() => setShowPlans(false)}
            className={`px-3 py-1 rounded-md text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} transition-colors`}
          >
            Back to Billing
          </button>
        </div>

        <PricingPlans currentSubscription={subscription} onSelectPlan={handleSelectPlan} />
      </div>
    );
  }

  return (
    <div>
      {successMessage && (
        <div className={`p-4 rounded-md mb-6 ${isDarkMode ? 'bg-green-900/30' : 'bg-green-50'}`}>
          <p className={`text-sm ${isDarkMode ? 'text-green-200' : 'text-green-700'}`}>
            <span className="flex items-center">
              <svg
                className="w-5 h-5 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              {successMessage}
              <button onClick={() => setSuccessMessage(null)} className="ml-auto text-sm underline">
                Dismiss
              </button>
            </span>
          </p>
        </div>
      )}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Billing Management
          </h2>
          <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Manage your subscription and billing information
          </p>
        </div>
        <div className="flex items-center space-x-4">
          {/* Only show View Plans button if no subscription exists */}
          {!subscription && (
            <button
              onClick={() => setShowPlans(true)}
              className={`px-4 py-2 rounded-md ${isDarkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white transition-colors`}
              disabled={processingAction}
            >
              {processingAction ? 'Processing...' : 'Subscribe Now'}
            </button>
          )}
          {/* Billing Portal button removed as requested */}
        </div>
      </div>

      {!subscription ? (
        <div className={`p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
          <div className="text-center py-8">
            <p className="mb-6 text-gray-500">
              You don't have an active subscription yet. Subscribe to a plan to get started.
            </p>
          </div>
        </div>
      ) : (
        <>
          <SubscriptionPlan
            subscription={subscription}
            onRefresh={fetchSubscription}
            onShowPlans={() => setShowPlans(true)}
          />

          <div className="mt-8">
            <h3 className="text-lg font-medium mb-4">Billing History</h3>
            <BillingHistory />
          </div>
        </>
      )}
    </div>
  );
};

export default BillingManagement;
